import React, { useEffect, useRef } from 'react';
import { Device, AudioProcessor } from '@twilio/voice-sdk';

let audioContext = new AudioContext();
let globalDestination: MediaStreamAudioDestinationNode | null = null;
let textToSpeechBytes: Record<string, any> = {};
let nextStartTime = audioContext.currentTime;

declare global {
  interface Window {
    onAudioIncoming: (data: Int16Array, sampleRate: number) => void;
    onTextToSpeech: (data: any) => void;
    onTwilioError: (error: string) => void;
    onInterpreterUpdate: (data: any) => void;
    interpreterUpdate: (data: any) => void;
  }
}

let activeSources: AudioBufferSourceNode[] = [];

const stopAllAudio = () => {
  for (const src of activeSources) {
    try { src.stop(); } catch (_) {}
  }

  activeSources = [];
  nextStartTime = audioContext.currentTime;
  textToSpeechBytes = {};
}

const playAudio = (audioBytes: Int16Array, sampleRate: number) => {
  if (!globalDestination) return;

  // Convert Int16 -> Float32
  const buffer = new Float32Array(audioBytes.length / 2);
  for (let i = 0; i < audioBytes.length; i += 2) {
    const value = audioBytes[i] + (audioBytes[i + 1] << 8);
    buffer[i / 2] =
      value >= 0x8000 ? -(0x10000 - value) / 0x8000 : value / 0x7fff;
  }

  const audioBuffer = audioContext.createBuffer(1, buffer.length, sampleRate);
  audioBuffer.copyToChannel(buffer, 0);

  const source = audioContext.createBufferSource();
  activeSources.push(source);
  source.buffer = audioBuffer;

  // GainNode for smooth fade
  const gainNode = audioContext.createGain();
  source.connect(gainNode).connect(globalDestination);

  source.onended = () => {
    activeSources = activeSources.filter(s => s !== source);
  };

  const now = audioContext.currentTime;
  if (nextStartTime < now) {
    nextStartTime = now; // reset if we're behind
  }

  // Avoid clicks with short fade in/out
  gainNode.gain.setValueAtTime(0, nextStartTime);
  gainNode.gain.linearRampToValueAtTime(1, nextStartTime + 0.01);
  gainNode.gain.setValueAtTime(1, nextStartTime + audioBuffer.duration - 0.01);
  gainNode.gain.linearRampToValueAtTime(0, nextStartTime + audioBuffer.duration);

  source.start(nextStartTime);

  // Schedule interpreter finished callback
  const finishTime = nextStartTime + audioBuffer.duration;
  setTimeout(() => {
    if (window.onInterpreterUpdate) {
      window.onInterpreterUpdate({ event: 'aiInterpreterFinished' });
    }
  }, (finishTime - now) * 1000);

  nextStartTime += audioBuffer.duration;
};

const onAudio = (
  data: Int16Array,
  language: string,
  id: string,
  part: number,
  maxParts: number,
  sampleRate: number = 16000
) => {
  if (!textToSpeechBytes[id]) {
    textToSpeechBytes[id] = {
      chunks: new Array(maxParts),
      chunksAdded: 0,
    };
  }

  textToSpeechBytes[id].chunks[part] = data;
  textToSpeechBytes[id].chunksAdded++;

  if (textToSpeechBytes[id].chunksAdded === maxParts) {
    const totalLength = textToSpeechBytes[id].chunks.reduce(
      (acc: number, arr: Int16Array) => acc + arr.length,
      0
    );
    const playSoundBytes = new Int16Array(totalLength);
    let currentIndex = 0;

    for (const int16Array of textToSpeechBytes[id].chunks) {
      playSoundBytes.set(int16Array, currentIndex);
      currentIndex += int16Array.length;
    }

    playAudio(playSoundBytes, sampleRate);

    delete textToSpeechBytes[id];
  }
};

class BackgroundAudioProcessor implements AudioProcessor {
  async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
    globalDestination = audioContext.createMediaStreamDestination();
    return globalDestination.stream;
  }

  async destroyProcessedStream(stream: MediaStream): Promise<void> {
    globalDestination?.disconnect();
    globalDestination = null;
  }
}

const ConferenceAgentListener: React.FC = () => {
  const device = useRef<Device | null>(null);

  window.onTextToSpeech = (message: any) => {
    const buffer = message.buffer;
    onAudio(buffer, message.langCode, message.id, message.part, message.maxParts, message.sampleRate);
  };

  window.interpreterUpdate = (data: any) => {
    if (data.event === 'interrupt') {       
       nextStartTime = audioContext.currentTime;
       stopAllAudio();
    }
  };    

  const float32ToS16 = (input: Float32Array) => {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const sample = Math.max(-1, Math.min(1, input[i]));
      output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return output.buffer;
  };

  const onError = (error: any) => {
    console.error('Error:', error);
    if (window.onTwilioError) {
      window.onTwilioError(error);
    }
  };

  const sendForProcessing = (stream: MediaStream) => {
    const sampleRate = audioContext.sampleRate;
    const bufferSize = 4096;

    const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
    processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const input = event.inputBuffer.getChannelData(0);
      const output = event.outputBuffer.getChannelData(0);
      output.set(input);
      const s16Data = float32ToS16(input);
      if (window.onAudioIncoming) {
        window.onAudioIncoming(new Int16Array(s16Data), sampleRate);
      }
    };

    const source = audioContext.createMediaStreamSource(stream);
    const destination = audioContext.createMediaStreamDestination();
    source.connect(processor);
    processor.connect(destination);
  };

  const setupDevice = async () => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let token = searchParams.get('token');
      const callId = searchParams.get('callId');

      if (!token || !callId) {
        console.error('Token is invalid');
        return;
      }

      device.current = new Device(token);

      const processor = new BackgroundAudioProcessor();
      await device.current.audio?.addProcessor(processor);

      const call = await device.current.connect({
        params: { CallId: callId },
      });

      call.on('error', (error) => {
        console.error('Call error:', error);
        onError(error.message);
      });

      call.on('disconnect', () => {
        console.log('Call disconnected');
      });

      call.on('accept', () => {
        console.log('Call accepted');
      });

      call.on('audio', async () => {
        console.log('audio ready');
        const remoteStream = await call.getRemoteStream();
        if (remoteStream) {
          sendForProcessing(remoteStream);
        }
      });

      call.on('ringing', () => {
        console.log('Call ringing');
      });
    } catch (error) {
      console.error('Failed to setup Twilio Device:', error);
      onError(error);
    }
  };

  useEffect(() => {
    setupDevice();

    window.addEventListener('click', () => {
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
    });
  }, []);

  return <p>Connected to the conference in listen-only mode.</p>;
};

export default ConferenceAgentListener;